import { useEffect, useState } from "react";
import Select from "react-select";
import { server } from "../../server";
import axios from "axios";
import { toast } from "react-toastify";
import { classList } from "../../utils";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const SOURCE_OPTIONS = [
  "Advertisement",
  "Online Front Site",
  "Google Ads",
  "Admission Campaign",
  "Front Office",
];

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "secondary" },
  { value: "contacted", label: "Contacted", color: "info" },
  { value: "interested", label: "Interested", color: "warning" },
  { value: "converted", label: "Converted", color: "success" },
  { value: "lost", label: "Lost", color: "danger" },
];

const getStatusMeta = (status) =>
  STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];

const wrapInLines = (text, perLine = 6) => {
  if (!text) return null;
  return text
    .split(" ")
    .reduce((acc, word, index) => {
      const last = acc.length - 1;
      if (index % perLine === 0) acc.push([word]);
      else acc[last].push(word);
      return acc;
    }, [])
    .map((group, i) => (
      <span key={i}>
        {group.join(" ")}
        <br />
      </span>
    ));
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const getLatestFollowUp = (enquiry) => {
  if (!enquiry.followUps || enquiry.followUps.length === 0) return null;
  return enquiry.followUps[enquiry.followUps.length - 1];
};

const getDueMeta = (nextFollowUpDate, status) => {
  if (!nextFollowUpDate || status === "converted" || status === "lost")
    return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(nextFollowUpDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0)
    return { label: `Overdue by ${Math.abs(diffDays)}d`, color: "danger" };
  if (diffDays === 0) return { label: "Due Today", color: "warning" };
  if (diffDays <= 3) return { label: `Due in ${diffDays}d`, color: "info" };
  return { label: `Upcoming`, color: "light text-dark" };
};

const emptyEnquiryForm = {
  name: "",
  class: "",
  phoneNo: "",
  email: "",
  source: "",
};
const emptyFollowUpFields = { date: "", remark: "", nextFollowUpDate: "" };

function AdmissionEnquiry() {
  const [enquiryForm, setEnquiryForm] = useState(emptyEnquiryForm);
  const [statusField, setStatusField] = useState("new");
  const [followUpFields, setFollowUpFields] = useState(emptyFollowUpFields);

  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [enquiries, setEnquiries] = useState([]);

  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const recordsPerPage = 10;

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [activeEnquiry, setActiveEnquiry] = useState(null);

  const totalFields =
    Object.keys(emptyEnquiryForm).length +
    Object.keys(emptyFollowUpFields).length;
  const filledFields =
    Object.values(enquiryForm).filter((v) => v.trim() !== "").length +
    Object.values(followUpFields).filter((v) => v.trim() !== "").length;

  const handleEnquiryFieldChange = (e) => {
    const { name, value } = e.target;
    setEnquiryForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleFollowUpFieldChange = (e) => {
    const { name, value } = e.target;
    setFollowUpFields((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const resetForm = () => {
    setEnquiryForm(emptyEnquiryForm);
    setFollowUpFields(emptyFollowUpFields);
    setStatusField("new");
    setErrors({});
    setIsEditing(false);
    setEditId(null);
    setActiveEnquiry(null);
  };

  const openAddEnquiryModal = () => {
    resetForm();
  };

  const openManageModal = (enquiry) => {
    setEnquiryForm({
      name: enquiry.name || "",
      class: enquiry.class || "",
      phoneNo: enquiry.phoneNo || "",
      email: enquiry.email || "",
      source: enquiry.source || "",
    });
    setStatusField(enquiry.status || "new");
    setFollowUpFields(emptyFollowUpFields);
    setErrors({});
    setIsEditing(true);
    setEditId(enquiry._id);
    setActiveEnquiry(enquiry);

    const modal = new window.bootstrap.Modal(
      document.querySelector("#enquiryModal"),
    );
    modal.show();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};
    Object.keys(enquiryForm).forEach((key) => {
      if (!enquiryForm[key].trim()) newErrors[key] = true;
    });
    if (isEditing && !statusField) newErrors.status = true;

    const followUpValues = Object.values(followUpFields);
    const followUpFilled = followUpValues.some((v) => v.trim() !== "");
    const followUpComplete = followUpValues.every((v) => v.trim() !== "");

    if (!isEditing || followUpFilled) {
      if (!followUpComplete) {
        Object.keys(followUpFields).forEach((key) => {
          if (!followUpFields[key].trim()) newErrors[key] = true;
        });
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);

    try {
      const url = isEditing
        ? `${server}/update_enquiry_mongodb`
        : `${server}/add_enquiry_mongodb`;

      const payload = isEditing
        ? {
            id: editId,
            ...enquiryForm,
            status: statusField,
            ...(followUpComplete ? { newFollowUp: followUpFields } : {}),
          }
        : { ...enquiryForm, ...followUpFields };

      await axios.post(url, payload, {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      toast.success(isEditing ? "Enquiry Updated" : "Enquiry Submitted", {
        position: "top-right",
      });

      document
        .querySelector('#enquiryModal [data-bs-dismiss="modal"]')
        ?.click();
      fetchEnquiryDetails();
      resetForm();
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setSubmitting(false);
    }
  };

  const fetchEnquiryDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${server}/enquiry_alldata_mongodb`, {
        headers: { Authorization: `${localStorage.getItem("token")}` },
      });
      const responseData = response.data?.body?.data;

      if (Array.isArray(responseData)) {
        const sortedEnquiries = responseData.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        setEnquiries(sortedEnquiries);
      } else {
        console.error("Invalid data format from API", response.data);
      }
    } catch (error) {
      console.error("Error fetching enquiry details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiryDetails();
  }, []);

  const matchesSearch = (enquiry) => {
    const searchLower = searchTerm.toLowerCase();
    if (!searchLower) return true;

    const baseFields = [
      enquiry.name,
      enquiry.class,
      enquiry.phoneNo,
      enquiry.email,
      enquiry.source,
    ];
    if (
      baseFields.some(
        (v) => typeof v === "string" && v.toLowerCase().includes(searchLower),
      )
    ) {
      return true;
    }
    return (enquiry.followUps || []).some(
      (f) =>
        typeof f.remark === "string" &&
        f.remark.toLowerCase().includes(searchLower),
    );
  };

  const filteredRecords = enquiries.filter((enquiry) => {
    const statusMatches =
      statusFilter === "all" || enquiry.status === statusFilter;
    return statusMatches && matchesSearch(enquiry);
  });

  const statusCounts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s.value] = enquiries.filter((e) => e.status === s.value).length;
    return acc;
  }, {});

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleDelete = async (phoneNoToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the enquiry for phone no. "${phoneNoToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/delete_enquiry_mongodb`,
          { phoneNo: phoneNoToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success("Admission Enquiry Deleted", { position: "top-right" });
          setEnquiries((prev) =>
            prev.filter((enquiry) => enquiry.phoneNo !== phoneNoToDelete),
          );
        } else {
          toast.error("Deletion failed. Please try again.");
        }
      } catch (error) {
        toast.error("Error occurred while deleting. Try again.");
      }
    }
  };

  const buildSheetRow = (enquiry) => {
    const latest = getLatestFollowUp(enquiry);
    return {
      Name: enquiry.name || "",
      Class: enquiry.class || "",
      "Phone No": enquiry.phoneNo || "",
      Email: enquiry.email || "",
      Source: enquiry.source || "",
      Status: getStatusMeta(enquiry.status).label,
      "Last Remark": latest?.remark || "",
      "Last Follow-up Date": latest ? formatDate(latest.date) : "",
      "Next Follow-up Date": latest ? formatDate(latest.nextFollowUpDate) : "",
      "Total Follow-ups": enquiry.followUps?.length || 0,
    };
  };

  const writeWorkbook = (sheetData, sheetName, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, fileName);
    toast.success("Report Generated");
  };

  const handleExportAllEnquiriesExcel = () => {
    if (!enquiries || enquiries.length === 0) {
      toast.error("No enquiry records to export.");
      return;
    }
    const sheetData = enquiries.map(buildSheetRow);
    const today = new Date().toISOString().slice(0, 10);
    writeWorkbook(
      sheetData,
      "All Enquiries",
      `AdmissionEnquiries_${today}.xlsx`,
    );
  };

  const handleExportTodaysEnquiriesExcel = () => {
    if (!enquiries || enquiries.length === 0) {
      toast.error("No enquiry records to export.");
      return;
    }

    const today = new Date().toISOString().split("T")[0];
    const todaysEnquiries = enquiries.filter((enquiry) => {
      const createdDate = enquiry.createdAt
        ? new Date(enquiry.createdAt).toISOString().split("T")[0]
        : null;
      return createdDate === today;
    });

    if (todaysEnquiries.length === 0) {
      toast.warn("No enquiries made today.");
      return;
    }

    const sheetData = todaysEnquiries.map(buildSheetRow);
    writeWorkbook(
      sheetData,
      "Today's Enquiries",
      `TodaysEnquiries_${today}.xlsx`,
    );
  };

  const handleExportPendingFollowUpsExcel = () => {
    if (!enquiries || enquiries.length === 0) {
      toast.error("No enquiry records to export.");
      return;
    }

    const pending = enquiries.filter((enquiry) => {
      const latest = getLatestFollowUp(enquiry);
      if (!latest) return false;
      const due = getDueMeta(latest.nextFollowUpDate, enquiry.status);
      return due !== null;
    });

    if (pending.length === 0) {
      toast.warn("No pending follow-ups.");
      return;
    }

    const sheetData = pending.map(buildSheetRow);
    const today = new Date().toISOString().slice(0, 10);
    writeWorkbook(
      sheetData,
      "Pending Follow-ups",
      `PendingFollowUps_${today}.xlsx`,
    );
  };

  const handleExportSourceWiseEnquiriesExcel = () => {
    if (!enquiries || enquiries.length === 0) {
      toast.error("No enquiry records to export.");
      return;
    }

    const groupedBySource = {};
    enquiries.forEach((enquiry) => {
      const source = enquiry.source || "Unknown";
      if (!groupedBySource[source]) groupedBySource[source] = [];
      groupedBySource[source].push(buildSheetRow(enquiry));
    });

    const workbook = XLSX.utils.book_new();
    Object.entries(groupedBySource).forEach(([source, records]) => {
      const worksheet = XLSX.utils.json_to_sheet(records);
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        source.length > 31 ? source.slice(0, 28) + "..." : source,
      );
    });

    const today = new Date().toISOString().split("T")[0];
    XLSX.writeFile(workbook, `SourceWiseEnquiries_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            className="modal fade"
            id="enquiryModal"
            tabIndex="-1"
            role="dialog"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header d-flex justify-content-between">
                    <h5 className="modal-title fs-4">
                      {isEditing ? "Manage Enquiry" : "Add Enquiry"}
                      {!isEditing && (
                        <span className="ms-3 text-secondary">
                          ({String(filledFields).padStart(2, "0")}/
                          {String(totalFields).padStart(2, "0")})
                        </span>
                      )}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    ></button>
                  </div>

                  <div className="modal-body">
                    {/* Core contact details */}
                    <div className="row g-2 mb-2">
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          className={`form-control text-dark ${errors.name ? "border-danger" : ""}`}
                          placeholder="Enter Name"
                          value={enquiryForm.name}
                          onChange={handleEnquiryFieldChange}
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Class <span className="text-danger">*</span>
                        </label>
                        <Select
                          name="class"
                          value={
                            classList
                              .map((cls) => ({ label: cls, value: cls }))
                              .find((opt) => opt.value === enquiryForm.class) ||
                            null
                          }
                          onChange={(selected) =>
                            handleEnquiryFieldChange({
                              target: {
                                name: "class",
                                value: selected?.value || "",
                              },
                            })
                          }
                          options={classList.map((cls) => ({
                            label: cls,
                            value: cls,
                          }))}
                          placeholder="Select Class"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Phone No <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="phoneNo"
                          className={`form-control text-dark ${errors.phoneNo ? "border-danger" : ""}`}
                          placeholder="Enter Phone No"
                          value={enquiryForm.phoneNo}
                          onChange={handleEnquiryFieldChange}
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Email ID <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="email"
                          className={`form-control text-dark ${errors.email ? "border-danger" : ""}`}
                          placeholder="Enter Email ID"
                          value={enquiryForm.email}
                          onChange={handleEnquiryFieldChange}
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Source <span className="text-danger">*</span>
                        </label>
                        <Select
                          name="source"
                          value={
                            SOURCE_OPTIONS.map((src) => ({
                              label: src,
                              value: src,
                            })).find(
                              (opt) => opt.value === enquiryForm.source,
                            ) || null
                          }
                          onChange={(selected) =>
                            handleEnquiryFieldChange({
                              target: {
                                name: "source",
                                value: selected?.value || "",
                              },
                            })
                          }
                          options={SOURCE_OPTIONS.map((src) => ({
                            label: src,
                            value: src,
                          }))}
                          placeholder="Select Source"
                        />
                      </div>
                      {isEditing && (
                        <div className="col-lg-6 col-12">
                          <label className="form-label mb-1 ms-3">
                            Status <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select text-dark ${errors.status ? "border-danger" : ""}`}
                            value={statusField}
                            onChange={(e) => setStatusField(e.target.value)}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Follow-up history (edit mode only) */}
                    {isEditing && activeEnquiry && (
                      <>
                        <hr className="mt-4 mb-3" />
                        <p className="text-secondary fw-medium mb-3">
                          Follow-up History (
                          {activeEnquiry.followUps?.length || 0})
                        </p>
                        <div
                          className="mb-2"
                          style={{ maxHeight: "220px", overflowY: "auto" }}
                        >
                          {(!activeEnquiry.followUps ||
                            activeEnquiry.followUps.length === 0) && (
                            <p className="text-secondary mb-0">
                              No follow-ups logged yet.
                            </p>
                          )}
                          {[...(activeEnquiry.followUps || [])]
                            .reverse()
                            .map((f, idx) => (
                              <div
                                key={idx}
                                className="border-start border-3 border-danger-subtle ps-3 mb-2"
                              >
                                <p className="mb-0 lh-sm">
                                  <span className="fw-semibold text-dark">
                                    {formatDate(f.date)}
                                  </span>
                                  <span className="text-secondary ms-2">
                                    next follow-up:{" "}
                                    {formatDate(f.nextFollowUpDate)}
                                  </span>
                                </p>
                                <p className="mb-0 text-dark">{f.remark}</p>
                              </div>
                            ))}
                        </div>
                      </>
                    )}

                    <hr />
                    <p className="text-secondary fw-medium mb-2">
                      {isEditing
                        ? "Log a New Follow-up (optional)"
                        : "First Follow-up"}
                    </p>
                    <div className="row g-2">
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Date{" "}
                          {!isEditing && <span className="text-danger">*</span>}
                        </label>
                        <input
                          type="date"
                          name="date"
                          className={`form-control text-dark ${errors.date ? "border-danger" : ""}`}
                          value={followUpFields.date}
                          onChange={handleFollowUpFieldChange}
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Next Follow Up Date{" "}
                          {!isEditing && <span className="text-danger">*</span>}
                        </label>
                        <input
                          type="date"
                          name="nextFollowUpDate"
                          className={`form-control text-dark ${errors.nextFollowUpDate ? "border-danger" : ""}`}
                          value={followUpFields.nextFollowUpDate}
                          onChange={handleFollowUpFieldChange}
                        />
                      </div>
                      <div className="col-lg-12 col-12">
                        <label className="form-label mb-1 ms-3">
                          Remark{" "}
                          {!isEditing && <span className="text-danger">*</span>}
                        </label>
                        <textarea
                          name="remark"
                          placeholder={
                            isEditing
                              ? "What happened in this interaction? (leave blank to skip)"
                              : "Enter Remark"
                          }
                          rows="3"
                          className={`form-control text-dark ${errors.remark ? "border-danger" : ""}`}
                          value={followUpFields.remark}
                          onChange={handleFollowUpFieldChange}
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Save"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table className="table mb-0 text-nowrap table-centered">
                    <thead className="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRecords.map((enquiry, index) => {
                        const latest = getLatestFollowUp(enquiry);
                        const dueMeta = latest
                          ? getDueMeta(latest.nextFollowUpDate, enquiry.status)
                          : null;
                        const statusMeta = getStatusMeta(enquiry.status);

                        return (
                          <tr key={enquiry._id}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-3 text-wrap">
                              <span className="text-dark fw-semibold text-capitalize">
                                {enquiry.name}
                              </span>
                              <span
                                className={`badge bg-${statusMeta.color}-soft ms-2 text-capitalize`}
                              >
                                {statusMeta.label}
                              </span>
                              <br />
                              <span className="text-dark">
                                Class
                              </span> &mdash; {enquiry.class} <br />
                              <span className="text-dark">Phone No</span>{" "}
                              &mdash; {enquiry.phoneNo} <br />
                              <span className="text-dark">
                                Source
                              </span> &mdash; {enquiry.source} <br />
                              {latest ? (
                                <>
                                  <span className="text-dark">Last Remark</span>{" "}
                                  &mdash; {wrapInLines(latest.remark)}
                                  <span className="text-dark">
                                    Last Follow-up
                                  </span>{" "}
                                  &mdash; {formatDate(latest.date)}
                                  <br />
                                  <span className="text-dark">
                                    Next Follow-up
                                  </span>{" "}
                                  &mdash; {formatDate(latest.nextFollowUpDate)}
                                  {dueMeta && (
                                    <span
                                      className={`badge bg-${dueMeta.color} ms-2`}
                                    >
                                      {dueMeta.label}
                                    </span>
                                  )}
                                  <br />
                                  <span className="text-secondary fs-8">
                                    {enquiry.followUps.length} follow-up
                                    {enquiry.followUps.length > 1
                                      ? "s"
                                      : ""}{" "}
                                    logged
                                  </span>
                                </>
                              ) : (
                                <span className="text-secondary">
                                  No follow-up logged yet
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span
                                  onClick={() => openManageModal(enquiry)}
                                  className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Manage
                                </span>
                                <span
                                  onClick={() => handleDelete(enquiry.phoneNo)}
                                  className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {currentRecords.length === 0 && (
                        <tr>
                          <td
                            colSpan="3"
                            className="text-center text-secondary py-4"
                          >
                            No enquiries match this view.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div>
                  <div className="mb-3">
                    <div className="d-flex bg-white justify-content-between rounded-4">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                      <button
                        type="button"
                        data-bs-toggle="modal"
                        data-bs-target="#enquiryModal"
                        onClick={openAddEnquiryModal}
                        className="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                      >
                        Add Enquiry
                      </button>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol className="breadcrumb pe-3">
                          <li className="breadcrumb-item">
                            <a href="#">Front Office</a>
                          </li>
                          <li
                            className="breadcrumb-item active"
                            aria-current="page"
                          >
                            Admission Enquiry
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {loading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" alt="" />
                        )}
                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          Admission Enquiry
                          <br />
                          <span className="text-secondary fs-6 fw-medium lh-sm">
                            0.4 MB Size
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div className="collapse" id="specifications">
                          <div className="py-3">
                            <div className="rounded-4 d-flex flex-wrap">
                              <span
                                onClick={handleExportAllEnquiriesExcel}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                All Enquiries
                              </span>
                              <span
                                onClick={handleExportTodaysEnquiriesExcel}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Today's Enquiry Report
                              </span>
                              <span
                                onClick={handleExportPendingFollowUpsExcel}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Pending Follow-ups
                              </span>
                              <span
                                onClick={handleExportSourceWiseEnquiriesExcel}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Source Wise Enquiries
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>
                  </div>

                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);
                          if (currentPage > 3)
                            pageNumbers.push("left-ellipsis");
                          if (currentPage !== 1 && currentPage !== totalPages)
                            pageNumbers.push(currentPage);
                          if (currentPage < totalPages - 2)
                            pageNumbers.push("right-ellipsis");
                          if (totalPages > 1) pageNumbers.push(totalPages);

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }
                            return (
                              <li
                                key={index}
                                className={`page-item ${currentPage === page ? "active" : ""}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}

                        <li
                          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default AdmissionEnquiry;
